// Plan calculation utilities for internet and electricity plans

// ============================================
// Usage Multipliers
// ============================================

export type UsageLevel = 'low' | 'moderate' | 'heavy';

export const USAGE_MULTIPLIERS: Record<UsageLevel, number> = {
  low: 0.85,
  moderate: 1.0,
  heavy: 1.2,
};

// Texas seasonal usage pattern (multipliers for each month Jan-Dec)
const SEASONAL_PATTERN = [0.7, 0.7, 0.8, 0.9, 1.1, 1.3, 1.5, 1.5, 1.3, 1.0, 0.8, 0.7];

/**
 * Generate synthetic 12-month usage based on average and Texas seasonal patterns
 */
export function generateSyntheticUsage(avgMonthly: number): number[] {
  return SEASONAL_PATTERN.map((factor) => Math.round(avgMonthly * factor));
}

/**
 * Calculate electricity usage from property data
 */
export function calculateElectricityUsage(squareFootage: number, yearBuilt?: number): number {
  if (!squareFootage || squareFootage <= 0) return 1000;

  // Texas baseline rate: 0.5 kWh per sq ft per month
  const baseRate = 0.5;

  // Calculate efficiency multiplier based on year built
  let efficiencyMultiplier = 1.0;

  if (!yearBuilt || yearBuilt < 1980) {
    efficiencyMultiplier = 1.25;
  } else if (yearBuilt >= 1980 && yearBuilt <= 1999) {
    efficiencyMultiplier = 1.1;
  } else if (yearBuilt >= 2000 && yearBuilt <= 2009) {
    efficiencyMultiplier = 1.0;
  } else if (yearBuilt >= 2010 && yearBuilt <= 2019) {
    efficiencyMultiplier = 0.9;
  } else {
    efficiencyMultiplier = 0.85;
  }

  return Math.round(squareFootage * baseRate * efficiencyMultiplier);
}

// ============================================
// Speed Score Colors & Messaging
// ============================================

/**
 * Get color for speed score based on percentile
 */
export function getScoreColor(score: number | null | undefined): string {
  if (score == null) return '#999';
  if (score >= 90) return '#2C9E5C'; // Dark Green - Excellent
  if (score >= 80) return '#8CDB9D'; // Light Green - Good
  if (score >= 65) return '#E9E687'; // Yellow - Adequate
  if (score >= 50) return '#FFBB87'; // Orange - Fair
  return '#F4A0A0'; // Red/Pink - Poor
}

/**
 * Get badge text for percentile score
 */
export function getPercentileBadgeText(percentile: number | null | undefined): string {
  if (percentile == null) return 'Internet Plan';

  if (percentile >= 95) {
    const rank = Math.ceil(100 - percentile);
    return `Top ${rank}% Speed`;
  }
  if (percentile >= 75) return 'Above Average Speed';
  if (percentile >= 50) return 'Average Speed';
  return 'Basic Speed';
}

/**
 * Get score messaging for modal display
 */
export function getScoreMessaging(score: number | null | undefined): string {
  if (score == null) return 'Speed data not available for this location';
  if (score >= 80) return 'Local data shows excellent speeds for your area';
  if (score >= 70) return 'Local data shows average speeds for your area';
  return 'Local data shows below average speeds for your area';
}

// ============================================
// Technology Capabilities
// ============================================

export type CapabilityRating = 'Excellent' | 'Good' | 'Adequate' | 'Poor';
export type CapabilityType = 'streaming' | 'multidevice' | 'gaming' | 'wholehome';

interface TechCapabilities {
  streaming: CapabilityRating;
  multidevice: CapabilityRating;
  gaming: CapabilityRating;
  wholehome: CapabilityRating;
}

const TECH_CAPABILITIES: Record<string, TechCapabilities> = {
  Fiber: { streaming: 'Excellent', multidevice: 'Excellent', gaming: 'Excellent', wholehome: 'Excellent' },
  Cable: { streaming: 'Excellent', multidevice: 'Good', gaming: 'Good', wholehome: 'Good' },
  'Fixed Wireless': { streaming: 'Good', multidevice: 'Adequate', gaming: 'Adequate', wholehome: 'Adequate' },
  DSL: { streaming: 'Adequate', multidevice: 'Poor', gaming: 'Poor', wholehome: 'Poor' },
  Copper: { streaming: 'Adequate', multidevice: 'Poor', gaming: 'Poor', wholehome: 'Poor' },
  Satellite: { streaming: 'Poor', multidevice: 'Poor', gaming: 'Poor', wholehome: 'Poor' },
};

const DEFAULT_CAPABILITIES: TechCapabilities = {
  streaming: 'Adequate',
  multidevice: 'Adequate',
  gaming: 'Adequate',
  wholehome: 'Adequate',
};

/**
 * Get capability ratings based on technology type
 */
export function getCapabilities(technology: string | null | undefined): TechCapabilities {
  if (!technology) return DEFAULT_CAPABILITIES;

  // Check for exact match first
  if (TECH_CAPABILITIES[technology]) {
    return TECH_CAPABILITIES[technology];
  }

  // Check for partial matches
  const techLower = technology.toLowerCase();
  if (techLower.includes('fiber')) return TECH_CAPABILITIES.Fiber;
  if (techLower.includes('cable')) return TECH_CAPABILITIES.Cable;
  if (techLower.includes('wireless')) return TECH_CAPABILITIES['Fixed Wireless'];
  if (techLower.includes('dsl') || techLower.includes('copper')) return TECH_CAPABILITIES.DSL;
  if (techLower.includes('satellite')) return TECH_CAPABILITIES.Satellite;

  return DEFAULT_CAPABILITIES;
}

/**
 * Get color for capability rating
 */
export function getCapabilityColor(rating: CapabilityRating): string {
  switch (rating) {
    case 'Excellent':
      return '#2C9E5C';
    case 'Good':
      return '#8CDB9D';
    case 'Adequate':
      return '#E9E687';
    case 'Poor':
      return '#F4A0A0';
  }
}

// ============================================
// Latency
// ============================================

export type LatencyLevel = 'Low' | 'Medium' | 'High';

/**
 * Get latency level based on technology
 */
export function getLatencyLevel(technology: string | null | undefined): LatencyLevel {
  if (!technology) return 'Medium';

  const techLower = technology.toLowerCase();
  if (techLower.includes('fiber') || techLower.includes('cable')) return 'Low';
  if (techLower.includes('wireless')) return 'Medium';
  return 'High'; // DSL, Copper, Satellite
}

/**
 * Get color for latency level
 */
export function getLatencyColor(level: LatencyLevel): string {
  switch (level) {
    case 'Low':
      return '#2C9E5C';
    case 'Medium':
      return '#E9E687';
    case 'High':
      return '#FFBB87';
  }
}

// ============================================
// Speed Formatting
// ============================================

/**
 * Format speed - convert Mbps to Gig if >= 1000
 */
export function formatSpeed(speedMbps: number | null | undefined): string {
  if (speedMbps == null) return 'Contact provider';

  if (speedMbps >= 1000) {
    const gig = speedMbps / 1000;
    return `${gig % 1 === 0 ? gig.toFixed(0) : gig.toFixed(1)} Gbps`;
  }
  return `${speedMbps} Mbps`;
}

/**
 * Format speed for title (lowercase)
 */
export function formatSpeedForTitle(speedMbps: number | null | undefined): string {
  if (speedMbps == null) return '';

  if (speedMbps >= 1000) {
    const gig = speedMbps / 1000;
    return `${gig % 1 === 0 ? gig.toFixed(0) : gig.toFixed(1)} gig`;
  }
  return `${speedMbps} mbps`;
}

// ============================================
// Month Labels
// ============================================

export const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Check if month index is a summer month (Jun-Sep, indices 5-8)
 */
export function isSummerMonth(index: number): boolean {
  return index >= 5 && index <= 8;
}
